import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EyeIcon } from '../../../../../assets/icons/eye-2-icon.svg';
import { useAppBranding } from '../../../../../config/branding';
import DefaultApiLimit from '../../../../../config/defaultLimit';
import {
  ApiRequestApprovedInterface,
  RequestApprovedInterface,
} from '../../../../../interface/request-approved-interface';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import {
  disableApprovedStationFilterApplied,
  fetchRequestApprovedStation,
  updateApprovedStationFilters,
  fetchRequestApprovedStationFilters,
  setSortData,
} from '../../../../../redux/slices/RequestApprovedLocationSlice';
import CustomTable from '../../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../../shared-components/success-notification/SuccessNotification';
import { GetParametersFromCommonFilters } from '../../../../../shared-functions/CommonFilters';
import { exportPDF, exportCSV } from '../../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../../shared-functions/ToFixedValue';
import UTCtDateoDateConvert from '../../../../../shared-functions/UTCtDateoDateConvert';
import penaltyManagementApi from '../../../../../utils/api/penalty-management';
import ViewOverrides from '../ViewOverrides';

interface ModelDetailsInterface {
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
  position?: string;
}

/**
 * Request approved component
 * @returns {any} - Request approved component
 */
function RequestApprovedStation() {
  const branding = useAppBranding();
  const { t } = useTranslation('penaltyManagement');
  const dispatch = useAppDispatch();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [isModelOpen, setIsModelOpen] = useState<boolean>(false);
  const [modelDetails, setModelDetails] = useState<ModelDetailsInterface>({
    title: '',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: false,
  });

  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const {
    requestApproved,
    limit,
    page,
    totalPages,
    totalResults,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
    loading,
  } = useAppSelector((state) => state.requestApprovedLocation);

  /**
   * function for closing modal
   * @param {string} message - Close message
   */
  const handleCloseModel = (message: string) => {
    if (message === 'success') {
      setIsModelOpen(false);
      setModelDetails({
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title={message}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => setIsModelOpen(false)}
          />
        ),
        rightCorner: '',
        notification: false,
      });
    } else {
      setIsModelOpen(false);
    }
  };

  /**
   * function for opening overlay
   * @param {any} currentData - The currentData
   */
  const handleOpenModel = (currentData: RequestApprovedInterface) => {
    const accountDetails = requestApproved.find((value: any) => value._id === currentData._id);
    if (accountDetails) {
      setIsModelOpen(true);
      setModelDetails({
        title: '',
        header: false,
        content: (
          <ViewOverrides
            type="Approved"
            currentData={accountDetails}
            closeModal={handleCloseModel}
          />
        ),
        rightCorner: '',
        notification: false,
      });
    }
  };
  /**
   * Renders custom data for the Dynamic Pricing column
   * @param {any} currentRow - The current row data
   * @returns {any} - The rendered dynamic pricing cell
   */
  const renderDynamicPricing = (currentRow: RequestApprovedInterface) => {
    return (
      <div className="dynamic-pricing-tags">
        {currentRow.dynamicPricing.map((item) => (
          <span key={item} className="pricing-chip">
            {item
              .replace(/([A-Z])/g, ' $1') // offPeak → off Peak
              .replace(/^./, (char) => char.toUpperCase())}
          </span>
        ))}
      </div>
    );
  };
  /**
   * Renders custom data for the Ceiling Type column
   * @param {any} currentRow - The current row data
   * @returns {any} - The rendered ceiling type cell
   */
  const renderCeilingType = (currentRow: RequestApprovedInterface) => {
    const formattedValue = currentRow.ceilingType
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (char) => char.toUpperCase());
    return <p>{formattedValue}</p>;
  };
  const column = [
    {
      label: t('accountId'),
      accessorKey: 'accountCustomId' as keyof RequestApprovedInterface,
      sortAllow: true,
    },
    {
      label: t('station'),
      accessorKey: 'stationNames' as keyof RequestApprovedInterface,
    },
    {
      label: t('dynamicPricing'),
      accessorKey: 'dynamicPricing' as keyof RequestApprovedInterface,
      customData: renderDynamicPricing,
      columnWidth: 200,
    },
    {
      label: t('graceInterval'),
      accessorKey: 'graceInterval' as keyof RequestApprovedInterface,
      sortAllow: true,
    },
    {
      label: t('maxCeilingValue'),
      accessorKey: 'ceilingValue' as keyof RequestApprovedInterface,
      sortAllow: true,
      /**
       * maxCeilingValue custom function
       * @param {any} currentRow - The currentRow
       */ customData: (currentRow: RequestApprovedInterface) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(currentRow.ceilingValue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('ceilingType'),
      accessorKey: 'ceilingType' as keyof RequestApprovedInterface,
      customData: renderCeilingType,
    },
    {
      label: t('effectiveDate'),
      accessorKey: 'effectiveFrom' as keyof RequestApprovedInterface,
      sortAllow: true,
      /**
       * customData
       * @param {any} customData - The customData
       */
      customData: (customData: RequestApprovedInterface) => (
        <div>
          {UTCtDateoDateConvert(customData.effectiveFrom)} -{' '}
          {UTCtDateoDateConvert(customData.effectiveTo)}
        </div>
      ),
    },
    {
      label: t('action'),
      accessorKey: '' as keyof RequestApprovedInterface,
      /**
       * Rendering action
       * @param {any} currentData - current row data
       * @returns {any} - action cell
       */
      customData: (currentData: RequestApprovedInterface) => (
        <div
          role="presentation"
          className="action-button"
          onClick={() => {
            handleOpenModel(currentData);
          }}>
          <EyeIcon />
        </div>
      ),
    },
  ];

  /**
   * Function for fetching request approved data
   * @param {any} data - Data
   */
  const fetchData = (data: ApiRequestApprovedInterface) => {
    if (!loading) {
      dispatch(fetchRequestApprovedStation(data));
    }
  };

  /**
   * Function for refreshing request approved data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortBy,
      sortField,
    });
  };

  /**
   * Function for sorting request approved data
   * @param {any} sortData sortData - Sort Data
   * @param {any} sortData.sortBy - The sortBy prop
   * @param {any} sortData.sortField - The sortField prop
   */
  const handleSort = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
    });
  };

  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('accountId'), accessorKey: 'accountCustomId' },
    { label: t('station'), accessorKey: 'stationNames' },
    { label: t('dynamicPricing'), accessorKey: 'dynamicPricing' },
    { label: t('graceInterval'), accessorKey: 'graceInterval' },
    { label: t('maxCeilingValue'), accessorKey: 'ceilingValue' },
    { label: t('ceilingType'), accessorKey: 'ceilingType' },
    { label: t('effectiveDate'), accessorKey: 'effectiveFrom' },
  ];

  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Function for exporting request approved data in pdf or csv format
   * @param {any} type - export type
   */
  const exportDataPDFOrCSV = (type: 'pdf' | 'csv') => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    setExportLoader(true);

    penaltyManagementApi
      .exportStationDataByStatus({
        payload,
        requestStatus: 'approved',
      })
      .then((response) => {
        const headers = exportCurrentColumns.map((col) => col.label);

        const rows = response.data.map((row: RequestApprovedInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'accountCustomId':
                return row.accountCustomId || t('na');

              case 'stationNames':
                return Array.isArray(row.stationNames)
                  ? row.stationNames.join(', ')
                  : row.stationNames || t('na');

              case 'dynamicPricing':
                return row.dynamicPricing?.length
                  ? row.dynamicPricing
                      .map((item) =>
                        item.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase()),
                      )
                      .join(', ')
                  : t('na');

              case 'graceInterval':
                return row.graceInterval ? `${row.graceInterval} Min` : t('na');

              case 'ceilingValue':
                return row.ceilingValue
                  ? `${ToFixedValue(row.ceilingValue)} ${branding.text.currency}`
                  : t('na');

              case 'ceilingType':
                return row.ceilingType
                  ? row.ceilingType
                      .replace(/([a-z])([A-Z])/g, '$1 $2')
                      .replace(/^./, (char) => char.toUpperCase())
                  : t('na');

              case 'effectiveFrom':
                return row?.effectiveFrom && row?.effectiveTo
                  ? `${UTCtDateoDateConvert(row.effectiveFrom)} - ${UTCtDateoDateConvert(row.effectiveTo)}`
                  : '-';

              default:
                return t('na');
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'request-approved-station');
          } else {
            exportPDF(headers, rows, 'request-approved-station');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Table pagination function
   * @param {any} value - Pagination value
   */
  const pageAndLimitChange = (value: ApiRequestApprovedInterface) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  useEffect(() => {
    if (isFilterUpdated || requestApproved.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableApprovedStationFilterApplied());
    }
  }, [filters]);

  return (
    <div>
      <ModalBox
        title={modelDetails.title}
        header={modelDetails.header}
        rightCorner={modelDetails.rightCorner}
        content={modelDetails.content}
        closeModal={() => setIsModelOpen(false)}
        open={isModelOpen}
        position="bottom-right"
      />
      <CustomTable
        version="v2"
        serialNo
        header={column}
        data={requestApproved}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        filterListLoading={filterListLoading}
        filters={filters}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSort}
        pageLimitChange={pageAndLimitChange}
        exportData={requestApproved}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPDFOrCSV}
        actionButton
        filtersToDispatch={updateApprovedStationFilters}
        filterListToDispatch={fetchRequestApprovedStationFilters}
        refreshRequired
        leftTopContent={<div />}
        sizeChangeButtonRequired
        hoverLabel={t('refresh')}
        refershCurrentPages={handleRefresh}
        loading={loading}
        height={window.innerHeight >= 700 ? 347 : 349}
        onHiddenColumnsChange={setHiddenColumns}
      />
    </div>
  );
}

export default RequestApprovedStation;
